import { useCallback, useEffect, useState } from "react";
import { defaultConfig } from "../types/pdf-generator.ts";
import {
  buildTdaMarkdown,
  downloadMarkdown,
} from "../utils/markdownGenerator.ts";
import TdaPdfGenerator from "../utils/tdaPdfGenerator.ts";

const STORAGE_KEY = "pdf-generator-config";

// Propriétés à exclure de la sauvegarde (références circulaires)
const EXCLUDED_PROPERTIES = [
  "generator",
  "pdfDoc",
  "rulesGenerator",
  "fonts",
  "pageCache",
  "doc",
];

// Fonction pour nettoyer l'objet de configuration
const cleanConfigForStorage = (config) => {
  const cleaned = { ...config };
  EXCLUDED_PROPERTIES.forEach((prop) => {
    delete cleaned[prop];
  });
  return cleaned;
};

// Fonctions utilitaires pour localStorage
const saveConfigToStorage = (config) => {
  try {
    const configToSave = cleanConfigForStorage(config);

    // Ne pas sauvegarder les presets temporaires
    if (configToSave.configPreset === "") {
      delete configToSave.configPreset;
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(configToSave));
    // console.log("✅ Configuration sauvegardée", configToSave);
  } catch (error) {
    console.warn("⚠️ Erreur sauvegarde localStorage:", error);
  }
};

const loadConfigFromStorage = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsedConfig = JSON.parse(saved);
      console.log("✅ Configuration chargée", parsedConfig);

      // Nettoyer la configuration chargée
      const cleanConfig = cleanConfigForStorage(parsedConfig);

      // Fusionner avec les valeurs par défaut pour gérer les nouvelles propriétés
      return {
        ...defaultConfig,
        ...cleanConfig,
        // Fusionner spécialement les objets imbriqués
        examples: {
          ...defaultConfig.examples,
          ...cleanConfig.examples,
        },
        indexOptions: {
          ...defaultConfig.indexOptions,
          ...cleanConfig.indexOptions,
        },
      };
    }
  } catch (error) {
    console.warn("⚠️ Erreur chargement localStorage:", error);
  }
  return defaultConfig;
};

const clearConfigFromStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log("🗑️ Configuration localStorage effacée");
  } catch (error) {
    console.warn("⚠️ Erreur suppression localStorage:", error);
  }
};

export const usePdfGenerator = () => {
  // Charger la config depuis localStorage au démarrage
  const [config, setConfig] = useState(() => loadConfigFromStorage());

  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: false,
    message: "",
  });
  const [generator] = useState(() => new TdaPdfGenerator());

  // Sauvegarder automatiquement à chaque modification de config
  useEffect(() => {
    saveConfigToStorage(config);
  }, [config]);

  // Mettre à jour la configuration
  const updateConfig = useCallback(
    (updates) => {
      setConfig((prev) => {
        const newConfig = { ...prev, ...updates };

        return newConfig;
      });
    },
    [generator]
  );

  const updatePreset = useCallback(
    (preset) => {
      // Gérer les presets
      if (preset) {
        const presetOptions = generator.applyPreset(preset);
        setConfig((prev) => ({
          ...prev,
          ...presetOptions,
        }));
      }
    },
    [generator]
  );

  // Réinitialiser la configuration
  const resetConfig = useCallback(() => {
    setConfig(defaultConfig);
    clearConfigFromStorage();
  }, []);

  // Réinitialiser le statut
  const resetStatus = useCallback(() => {
    setStatus({
      loading: false,
      success: false,
      error: false,
      message: "",
    });
  }, []);

  // Load rules
  const loadRules = useCallback(async () => {
    const rules = await generator.loadTdaRules(config.version, config.lang);
    return rules;
  }, [config, generator]);

  // Générer le PDF
  const generatePdf = useCallback(async () => {
    try {
      setStatus({
        loading: true,
        success: false,
        error: false,
        message: "",
      });

      // console.log("Options de génération:", config);

      const filenameBase = `tda-rules-${config.version}-${config.lang || "en"}`;

      if (config.outputFormat === "markdown") {
        const rules = await generator.loadTdaRules(
          config.version,
          config.lang || "en"
        );
        const markdown = buildTdaMarkdown(rules, config);
        downloadMarkdown(markdown, `${filenameBase}.md`);
      } else {
        const pdfBytes = await generator.generatePdf(config);
        generator.downloadPdf(pdfBytes, `${filenameBase}.pdf`);
      }

      setStatus({
        loading: false,
        success: true,
        error: false,
        message: "",
      });
    } catch (error) {
      console.error("Erreur lors de la génération:", error);
      setStatus({
        loading: false,
        success: false,
        error: true,
        message: error.message || "Erreur lors de la génération du PDF",
      });
    }
  }, [config, generator]);

  return {
    config,
    updateConfig,
    updatePreset,
    resetConfig,
    status,
    generatePdf,
    resetStatus,
    loadRules,
  };
};
